// The host end of a single surface iframe. Pure and framework-agnostic so the
// security-critical logic (handshake, theme-in, instanceId correlation) is
// fully unit-tested without a real iframe. SurfaceHost.tsx wires this to a real
// contentWindow + window message events. See the plan's security model.

import {
  SURFACE_CHANNEL,
  isSurfaceFrame,
  type HostToSurfaceFrame,
  type ThemeSnapshot,
} from '@slate/plugin-api';

/** The curated set of theme custom properties snapshotted into a surface, so we
 *  never dump arbitrary computed style across the boundary. */
export const THEME_VAR_NAMES: readonly string[] = [
  '--mp-font-ui',
  '--mp-font-serif',
  '--mp-font-mono',
  '--mp-font-reading',
  '--mp-font-prose',
  '--mp-bg',
  '--mp-bg-elev',
  '--mp-bg-sunken',
  '--mp-border',
  '--mp-text',
  '--mp-text-muted',
  '--mp-accent',
  '--mp-accent-fg',
  '--mp-code-bg',
  '--mp-selection',
  '--mp-danger',
  '--mp-radius',
  '--mp-radius-sm',
];

/** Snapshot the curated theme vars via an injected reader (test seam). */
export function snapshotThemeVars(
  read: (name: string) => string,
  names: readonly string[] = THEME_VAR_NAMES,
): ThemeSnapshot {
  const out: ThemeSnapshot = {};
  for (const name of names) {
    const value = read(name).trim();
    if (value) out[name] = value;
  }
  return out;
}

export interface SurfaceFramePort {
  /** contentWindow.postMessage(frame, '*'); '*' is forced by the opaque origin. */
  post(frame: HostToSurfaceFrame): void;
}

export interface SurfaceBridgeOptions {
  instanceId: string;
  theme: ThemeSnapshot;
  port: SurfaceFramePort;
  /** An inbound plugin payload from the iframe, to be delivered to the worker. */
  onMessageToWorker: (message: unknown) => void;
}

export class SurfaceBridge {
  private disposed = false;
  constructor(private readonly opts: SurfaceBridgeOptions) {}

  /** Handle a frame the caller has ALREADY source-matched to this iframe. */
  handleFrame(data: unknown): void {
    if (this.disposed) return;
    if (!isSurfaceFrame(data)) return;
    const frame = data;
    if (frame.t === 'ready') {
      this.opts.port.post({
        channel: SURFACE_CHANNEL,
        t: 'init',
        instanceId: this.opts.instanceId,
        theme: this.opts.theme,
      });
      return;
    }
    if (frame.t === 'message') {
      // Defense in depth: a frame must carry our instanceId.
      if (frame.instanceId !== this.opts.instanceId) return;
      this.opts.onMessageToWorker(frame.message);
    }
    // `init` from the iframe is ignored: the host never accepts an init.
  }

  /** Push a worker-originated message down to the iframe. */
  sendToFrame(message: unknown): void {
    if (this.disposed) return;
    this.opts.port.post({
      channel: SURFACE_CHANNEL,
      t: 'message',
      instanceId: this.opts.instanceId,
      message,
    });
  }

  dispose(): void {
    this.disposed = true;
  }
}

export interface FrameConnection {
  dispose(): void;
}

/** Wire a window 'message' listener that forwards ONLY frames whose source is
 *  the given iframe window into the bridge. The opaque sandbox origin makes
 *  origin strings unreliable, so source identity is the correlation. */
export function connectSurfaceFrame(params: {
  hostWindow: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  frameWindow: unknown;
  bridge: SurfaceBridge;
}): FrameConnection {
  const { hostWindow, frameWindow, bridge } = params;
  const listener = (e: Event): void => {
    const me = e as MessageEvent;
    if (me.source !== frameWindow) return;
    bridge.handleFrame(me.data);
  };
  hostWindow.addEventListener('message', listener);
  return { dispose: () => hostWindow.removeEventListener('message', listener) };
}
